import { createPortal } from 'preact/compat';
import { CARD_AUTOCOMPLETE_LIMIT } from '../utils/cardMentions';
import { CONTENT_BG_DEFAULT, MENU_BG } from '../lib/medievalTheme';

// Renders the @-mention card suggestion list, anchored against a
// canvas-local rect (from rectToCanvasRect). Portaled to .app-canvas
// because position:fixed is scoped to the canvas (transform:
// translateZ(0) on .app-canvas makes it a containing block), and the
// rect we're given is already canvas-local.
//
// `anchor` is the rect to attach to (caret rect for contenteditable
// surfaces; input-element rect for chat textareas/inputs). `placeAbove`
// is the sticky placement decision computed by the caller via
// choosePopoverSide so the popover doesn't flip on every keystroke as
// the result count shifts.
export default function CardMentionPopover({
  results,
  selected,
  anchor,
  placeAbove,
  onHover,
  onSelect,
}) {
  if (!anchor || !results || results.length === 0) return null;
  const { canvasWidth, canvasHeight } = anchor;
  const cqw = canvasWidth / 100;
  const cqh = canvasHeight / 100;

  // Mirror the renderAutocomplete math in MarkdownEditor so card-link
  // mentions in deck notes and in chat use one popover style + cap.
  const rowH = 2.75 * cqw;
  const listPadding = 0.5 * cqw;
  const listCssCap = 22 * cqh;
  const visibleCount = Math.min(results.length, CARD_AUTOCOMPLETE_LIMIT);
  const naturalH = Math.min(visibleCount * rowH + listPadding, listCssCap);

  const gap = 0.4 * cqw;
  const edgePad = 0.5 * cqw;
  const spaceBelow = canvasHeight - anchor.bottom - edgePad - gap;
  const spaceAbove = anchor.top - edgePad - gap;
  const sideSpace = placeAbove ? spaceAbove : spaceBelow;
  const popoverH = Math.max(rowH + listPadding, Math.min(naturalH, sideSpace));

  const popoverWidthCqw = 20;
  const popoverWidthPx = popoverWidthCqw * cqw;
  const left = Math.max(
    edgePad,
    Math.min(anchor.left, canvasWidth - popoverWidthPx - edgePad),
  );
  const top = placeAbove ? anchor.top - popoverH - gap : anchor.bottom + gap;
  const POPOVER_BORDER_PX = 1;
  const listMaxH = popoverH - 2 * POPOVER_BORDER_PX;

  // Background composes the same stone / chisel / scratches / cracks
  // texture stack used by the deck-notes panel and dice-roller modal
  // (TEXTURED_PANEL_STYLE recipe), so the dropdown reads as the same
  // carved-stone surface rather than a flat black overlay. Blend mode
  // `overlay, normal` lets the stone layer mix with the opaque MENU_BG coat beneath
  // instead of sitting opaquely on top.
  const node = (
    <div
      className="md-autocomplete"
      style={{
        position: 'fixed',
        top,
        left,
        width: `${popoverWidthCqw}cqw`,
        maxHeight: `${popoverH}px`,
        zIndex: 260,
        // Opaque base (MENU_BG, the same coat as the Select dropdowns): a
        // list floating over the board must never read as see-through.
        background: `${CONTENT_BG_DEFAULT}, ${MENU_BG}`,
        backgroundBlendMode: 'overlay, normal',
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="md-autocomplete-list" style={{ maxHeight: `${listMaxH}px` }}>
        {results.map((card, idx) => {
          const printing = card.printings?.[0];
          const isActive = idx === selected;
          return (
            <button
              key={card.unique_id}
              type="button"
              className={`md-autocomplete-item ${isActive ? 'is-active' : ''}`}
              onMouseEnter={() => onHover?.(idx)}
              onClick={() => onSelect?.(card)}
            >
              {printing?.image_url ? (
                <img
                  className="md-autocomplete-thumb"
                  src={printing.image_url}
                  alt=""
                  draggable={false}
                />
              ) : (
                <span className="md-autocomplete-thumb md-autocomplete-thumb-fallback">◆</span>
              )}
              <span className="md-autocomplete-label">
                <span className="md-autocomplete-name">{card.name}</span>
                {card.type ? <span className="md-autocomplete-meta">{card.type}</span> : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const canvas = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
  return canvas ? createPortal(node, canvas) : node;
}

// Sticky placement decision. Once a popover opens for a given @-
// mention session, callers should keep the chosen side until the
// session ends (mention closed, or caret left the @-span). Otherwise
// the popover flips below↔above on every keystroke that crosses the
// "fits naturally" threshold.
export function choosePopoverSide(anchor, resultCount) {
  if (!anchor) return false;
  const { canvasWidth, canvasHeight } = anchor;
  const cqw = canvasWidth / 100;
  const cqh = canvasHeight / 100;
  const rowH = 2.75 * cqw;
  const listPadding = 0.5 * cqw;
  const listCssCap = 22 * cqh;
  const visibleCount = Math.min(resultCount, CARD_AUTOCOMPLETE_LIMIT);
  const naturalH = Math.min(visibleCount * rowH + listPadding, listCssCap);
  const gap = 0.4 * cqw;
  const edgePad = 0.5 * cqw;
  const spaceBelow = canvasHeight - anchor.bottom - edgePad - gap;
  const spaceAbove = anchor.top - edgePad - gap;
  return spaceBelow < naturalH && spaceAbove > spaceBelow;
}
